import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import api from "../../utils/api";
import { getError } from "../../utils/getError";
import {
  IDLE_CONFIG,
  resolveEffectiveSpeed,
  odometerKmFromParams,
} from "../shared/vehicleStatusRule";
import { resolveMetricsFromParams } from "../../utils/vehicleParamsResolver";

const MAX_GAP_MS = 15 * 60 * 1000;

// Estado derivado por punto del recorrido
export type RouteStatus = "driving" | "ralenti" | "stopped";

// Colores por estado (deben coincidir con la lógica de VehicleCard)
export const ROUTE_STATUS_COLORS: Record<RouteStatus, string> = {
  driving: "#22C55E",
  ralenti: "#F59E0B",
  stopped: "#EF4444",
};
export const ROUTE_PANIC_COLOR = "#DC2626";

const PANIC_CODES = new Set<number>([5, 45]);

// Punto del recorrido histórico con su estado
export interface RoutePoint {
  lat: number;
  lng: number;
  ts: number; // epoch ms
  speed: number | null;
  heading: number | null;
  ignition: boolean | null;
  battery: number | null;
  altitude: number | null;
  eventCode: number | null;
  engineStatus: number | null;
  gpsSat: number | null;
  odoKm: number | null;
  idle: boolean | null;
  status: RouteStatus;
  isPanic: boolean;
}

export interface VehicleTracking {
  vehicleId: string; // device id
  vehicleName: string;
  color: string;
  points: RoutePoint[];
  startPoint: RoutePoint | null;
  endPoint: RoutePoint | null;
  from: string | null;
  to: string | null;
  total: number;
  tz: string | null;
  loading: boolean;
  error: string | null;
}

interface VehicleRouteTrackingState {
  activeTrackings: Record<string, VehicleTracking>;
}

interface VehicleRouteTrackingActions {
  loadHistory: (
    deviceId: string,
    vehicleName: string,
    color: string,
    tz?: string,
    brandName?: string | null,
    brandModel?: string | null,
  ) => Promise<void>;
  stopTracking: (vehicleId: string) => void;
  setColor: (vehicleId: string, color: string) => void;
  getTracking: (vehicleId: string) => VehicleTracking | undefined;
  isTracking: (vehicleId: string) => boolean;
}

type VehicleRouteTrackingStore = VehicleRouteTrackingState &
  VehicleRouteTrackingActions;

export const classifyPoint = (
  speed: number | null,
  engineStatus: number | null,
  ignition: boolean | null,
  idle: boolean | null,
): RouteStatus => {
  if (speed != null && speed > IDLE_CONFIG.movementThreshold) {
    return "driving";
  }
  if (idle === true) return "ralenti";
  if (idle === false) return "stopped";
  const isEngineOn =
    engineStatus === 1 ||
    engineStatus === 2 ||
    engineStatus === 3 ||
    ignition === true;
  if (isEngineOn) return "ralenti";
  return "stopped";
};

export const reclassifyIdleRuns = (points: RoutePoint[]): void => {
  const n = points.length;
  let i = 0;
  while (i < n) {
    if (points[i].status !== "ralenti" || points[i].idle === true) {
      i++;
      continue;
    }
    let j = i + 1;
    while (
      j < n &&
      points[j].status === "ralenti" &&
      points[j].idle !== true &&
      points[j].ts - points[j - 1].ts <= MAX_GAP_MS
    ) {
      j++;
    }
    const durationMs = points[j - 1].ts - points[i].ts;
    if (durationMs < IDLE_CONFIG.idleThreshold) {
      for (let k = i; k < j; k++) {
        points[k].status = "stopped";
      }
    }
    i = j;
  }
};

export interface ApiTrackingPoint {
  ts: number;
  lat: number;
  lng: number;
  speed?: number | null;
  heading?: number | null;
  ignition?: boolean | null;
  battery?: number | null;
  altitude?: number | null;
  event_code?: number | null;
  brand_name?: string | null;
  brand_model?: string | null;
  params?:
    | ({
        engine_status?: number | null;
        gps_sat?: number | null;
        odo_km?: number | string | null;
        virtual_odometer?: number | string | null;
        total_odometer?: number | string | null;
        "ignition(din4)"?: number | string | null;
      } & Record<string, number | string | null | undefined>)
    | null;
}

export const mapApiPoint = (
  p: ApiTrackingPoint,
  brandName?: string | null,
  brandModel?: string | null,
): RoutePoint => {
  const speed = p.speed ?? null;
  const engineStatus = p.params?.engine_status ?? null;
  const ignition = p.ignition ?? null;
  const eventCode = p.event_code ?? null;
  const resolvedName = p.brand_name ?? brandName ?? null;
  const resolvedModel = p.brand_model ?? brandModel ?? null;
  const idle = resolveMetricsFromParams(p.params, resolvedModel, resolvedName).idle;
  return {
    lat: p.lat,
    lng: p.lng,
    ts: p.ts,
    speed,
    heading: p.heading ?? null,
    ignition,
    battery: p.battery ?? null,
    altitude: p.altitude ?? null,
    eventCode,
    engineStatus,
    gpsSat: p.params?.gps_sat ?? null,
    odoKm: odometerKmFromParams(p.params),
    idle,
    status: classifyPoint(speed, engineStatus, ignition, idle),
    isPanic: eventCode != null && PANIC_CODES.has(Number(eventCode)),
  };
};

// Cuando un punto no trae `speed`, se estima la velocidad con el odómetro
// (Δodo / Δtiempo) usando el punto previo; si supera el umbral de movimiento se
// promueve a "driving". Igual que en tiempo real (resolveEffectiveSpeed).
export const applyImpliedSpeed = (points: RoutePoint[]): void => {
  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    if (p.speed != null) continue;
    const prev = points[i - 1];
    const implied = resolveEffectiveSpeed(
      p.speed,
      prev.odoKm,
      p.odoKm,
      prev.lat,
      prev.lng,
      p.lat,
      p.lng,
      prev.ts,
      p.ts,
    );
    if (implied != null && implied > IDLE_CONFIG.movementThreshold) {
      p.status = "driving";
    }
  }
};

// Post-proceso estándar de una ruta ya ordenada por ts: primero estima
// movimiento por odómetro, luego colapsa los ralentí más cortos que
// IDLE_CONFIG.idleThreshold a "stopped" (con el umbral en 0 no colapsa ninguno).
export const finalizeRoutePoints = (points: RoutePoint[]): void => {
  applyImpliedSpeed(points);
  reclassifyIdleRuns(points);
};

export const useVehicleRouteTrackingStore = create<VehicleRouteTrackingStore>()(
  (set, get) => ({
    activeTrackings: {},

    loadHistory: async (deviceId, vehicleName, color, tz, brandName, brandModel) => {
      const resolvedTz =
        tz || Intl.DateTimeFormat().resolvedOptions().timeZone;

      const prev = get().activeTrackings[deviceId];

      // Marcar como cargando inmediatamente (también activa el tracking)
      set((state) => ({
        activeTrackings: {
          ...state.activeTrackings,
          [deviceId]: {
            vehicleId: deviceId,
            vehicleName,
            color,
            points: prev?.points ?? [],
            startPoint: prev?.startPoint ?? null,
            endPoint: prev?.endPoint ?? null,
            from: prev?.from ?? null,
            to: prev?.to ?? null,
            total: prev?.total ?? 0,
            tz: resolvedTz,
            loading: true,
            error: null,
          },
        },
      }));

      try {
        const res = await api.get(
          `/tracking/points/${encodeURIComponent(deviceId)}`,
          {
            params: { tz: resolvedTz },
          },
        );

        const payload = res.data?.data ?? res.data;
        const rawPoints: ApiTrackingPoint[] = payload?.points ?? [];

        const points = rawPoints
          .map((p) => mapApiPoint(p, brandName, brandModel))
          .filter((p) => p.lat != null && p.lng != null)
          .sort((a, b) => a.ts - b.ts);

        finalizeRoutePoints(points);

        set((state) => {
          const current = state.activeTrackings[deviceId];
          // Si el usuario apagó el tracking mientras cargaba, no reescribir
          if (!current) return state;
          return {
            activeTrackings: {
              ...state.activeTrackings,
              [deviceId]: {
                ...current,
                points,
                startPoint: points[0] ?? null,
                endPoint: points[points.length - 1] ?? null,
                from: payload?.from ?? null,
                to: payload?.to ?? null,
                total: payload?.total ?? points.length,
                loading: false,
                error: null,
              },
            },
          };
        });
      } catch (err) {
        set((state) => {
          const current = state.activeTrackings[deviceId];
          if (!current) return state;
          return {
            activeTrackings: {
              ...state.activeTrackings,
              [deviceId]: {
                ...current,
                loading: false,
                error: getError(err),
              },
            },
          };
        });
      }
    },

    stopTracking: (vehicleId) => {
      set((state) => {
        const { [vehicleId]: _, ...rest } = state.activeTrackings;
        return { activeTrackings: rest };
      });
    },

    setColor: (vehicleId, color) => {
      set((state) => {
        const tracking = state.activeTrackings[vehicleId];
        if (!tracking) return state;
        return {
          activeTrackings: {
            ...state.activeTrackings,
            [vehicleId]: { ...tracking, color },
          },
        };
      });
    },

    getTracking: (vehicleId) => get().activeTrackings[vehicleId] ?? undefined,

    isTracking: (vehicleId) => vehicleId in get().activeTrackings,
  }),
);

// ─── Selectores ──────────────────────────────────────────────────────────────

export const useIsTracking = (vehicleId: string) =>
  useVehicleRouteTrackingStore((state) => vehicleId in state.activeTrackings);

export const useTrackingPointsCount = (vehicleId: string) =>
  useVehicleRouteTrackingStore(
    (state) => state.activeTrackings[vehicleId]?.points.length ?? 0,
  );

export const useTrackingMeta = (vehicleId: string) =>
  useVehicleRouteTrackingStore(
    useShallow((state) => {
      const t = state.activeTrackings[vehicleId];
      return {
        loading: t?.loading ?? false,
        error: t?.error ?? null,
        from: t?.from ?? null,
        to: t?.to ?? null,
        total: t?.total ?? 0,
      };
    }),
  );

// ─── Helpers para el mapa ────────────────────────────────────────────────────

export interface RouteSegment {
  status: RouteStatus;
  color: string;
  path: { lat: number; lng: number }[];
}

export interface RouteWaypoint {
  lat: number;
  lng: number;
  status: RouteStatus;
  color: string;
  isPanic: boolean;
  seq: number; // número de orden a lo largo del recorrido (1..N)
  ts: number;
  speed: number | null;
}

export interface RouteStateMarker {
  lat: number;
  lng: number;
  status: RouteStatus;
  color: string;
  ts: number;
  durationMs: number;
}

// Máximo de waypoints numerados que dibujamos para mantener el mapa legible.
const MAX_WAYPOINTS = 60;

const HARD_WAYPOINT_CAP = 80;

const MIN_STATE_DWELL_MS = 60_000;

const SEGMENT_SIMPLIFY_TOL_SQ = 1.6e-9;

const perpDistanceSq = (
  p: { lat: number; lng: number },
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number => {
  const dx = b.lng - a.lng;
  const dy = b.lat - a.lat;
  if (dx === 0 && dy === 0) {
    const ex = p.lng - a.lng;
    const ey = p.lat - a.lat;
    return ex * ex + ey * ey;
  }
  const t =
    ((p.lng - a.lng) * dx + (p.lat - a.lat) * dy) / (dx * dx + dy * dy);
  const cx = a.lng + t * dx;
  const cy = a.lat + t * dy;
  const ex = p.lng - cx;
  const ey = p.lat - cy;
  return ex * ex + ey * ey;
};

const simplifyPath = (
  path: { lat: number; lng: number }[],
  toleranceSq: number,
): { lat: number; lng: number }[] => {
  if (path.length <= 2) return path;
  const keep = new Uint8Array(path.length);
  keep[0] = 1;
  keep[path.length - 1] = 1;
  const stack: Array<[number, number]> = [[0, path.length - 1]];
  while (stack.length > 0) {
    const [start, end] = stack.pop()!;
    let maxDist = 0;
    let index = -1;
    for (let i = start + 1; i < end; i++) {
      const d = perpDistanceSq(path[i], path[start], path[end]);
      if (d > maxDist) {
        maxDist = d;
        index = i;
      }
    }
    if (maxDist > toleranceSq && index !== -1) {
      keep[index] = 1;
      stack.push([start, index]);
      stack.push([index, end]);
    }
  }
  const result: { lat: number; lng: number }[] = [];
  for (let i = 0; i < path.length; i++) {
    if (keep[i]) result.push(path[i]);
  }
  return result;
};

// Construye los tramos coloreados por estado (para la polyline) y una lista de
// waypoints numerados en orden. Los waypoints se muestrean para no saturar el
// mapa, pero SIEMPRE conservan inicio, fin, cambios de estado y eventos de
// pánico (los puntos relevantes del recorrido).
export const buildRouteSegments = (
  points: RoutePoint[],
): {
  segments: RouteSegment[];
  waypoints: RouteWaypoint[];
  stateMarkers: RouteStateMarker[];
} => {
  const segments: RouteSegment[] = [];

  if (points.length === 0)
    return { segments, waypoints: [], stateMarkers: [] };

  let currentSegment: RouteSegment | null = null;

  // 1. Tramos coloreados por estado
  points.forEach((point) => {
    const coord = { lat: point.lat, lng: point.lng };
    if (!currentSegment || currentSegment.status !== point.status) {
      const seed =
        currentSegment && currentSegment.path.length > 0
          ? [currentSegment.path[currentSegment.path.length - 1]]
          : [];
      currentSegment = {
        status: point.status,
        color: ROUTE_STATUS_COLORS[point.status],
        path: [...seed, coord],
      };
      segments.push(currentSegment);
    } else {
      currentSegment.path.push(coord);
    }
  });

  segments.forEach((s) => {
    if (s.path.length > 2) {
      s.path = simplifyPath(s.path, SEGMENT_SIMPLIFY_TOL_SQ);
    }
  });

  // 2. Índices a numerar: muestreo uniforme + puntos relevantes
  const n = points.length;
  const step = Math.max(1, Math.ceil(n / MAX_WAYPOINTS));
  const idxSet = new Set<number>();

  idxSet.add(0); // inicio
  idxSet.add(n - 1); // fin
  for (let i = 0; i < n; i += step) idxSet.add(i);
  for (let i = 1; i < n; i++) {
    if (points[i].status !== points[i - 1].status || points[i].isPanic) {
      idxSet.add(i);
    }
  }

  let orderedIdx = Array.from(idxSet).sort((a, b) => a - b);

  if (orderedIdx.length > HARD_WAYPOINT_CAP) {
    const mustKeep = new Set<number>([0, n - 1]);
    for (let i = 0; i < n; i++) {
      if (points[i].isPanic) mustKeep.add(i);
    }
    const optional = orderedIdx.filter((i) => !mustKeep.has(i));
    const keepCount = Math.max(0, HARD_WAYPOINT_CAP - mustKeep.size);
    const sampled = new Set<number>(mustKeep);
    if (keepCount > 0 && optional.length > 0) {
      const stride = optional.length / keepCount;
      for (let k = 0; k < keepCount; k++) {
        sampled.add(optional[Math.floor(k * stride)]);
      }
    }
    orderedIdx = Array.from(sampled).sort((a, b) => a - b);
  }

  const waypoints: RouteWaypoint[] = orderedIdx.map((idx) => {
    const point = points[idx];
    return {
      lat: point.lat,
      lng: point.lng,
      status: point.status,
      color: point.isPanic
        ? ROUTE_PANIC_COLOR
        : ROUTE_STATUS_COLORS[point.status],
      isPanic: point.isPanic,
      // Número = posición real dentro del recorrido completo (1..total),
      // por eso el último marcador coincide con "puntos registrados".
      seq: idx + 1,
      ts: point.ts,
      speed: point.speed,
    };
  });

  const stateMarkers: RouteStateMarker[] = [];
  let runStart = 0;
  for (let i = 1; i <= n; i++) {
    const ended = i === n || points[i].status !== points[runStart].status;
    if (!ended) continue;

    const status = points[runStart].status;
    if (status === "stopped" || status === "ralenti") {
      const first = points[runStart];
      const last = points[i - 1];
      const durationMs = last.ts - first.ts;
      if (durationMs >= MIN_STATE_DWELL_MS) {
        const mid = points[Math.floor((runStart + i - 1) / 2)];
        stateMarkers.push({
          lat: mid.lat,
          lng: mid.lng,
          status,
          color: ROUTE_STATUS_COLORS[status],
          ts: first.ts,
          durationMs,
        });
      }
    }
    runStart = i;
  }

  return { segments, waypoints, stateMarkers };
};
